/**
 * Currency Utilities
 * 
 * Core functions for currency conversion, normalization, and formatting
 */

import { CurrencyCode, Money, ParsedCurrency, CurrencyDisplayOptions, ExchangeRates } from '../types/currency.types';
import { EXCHANGE_RATES, CURRENCY_SYMBOLS, CURRENCY_PATTERNS, DEFAULT_CURRENCY } from '../constants/currencies';
import { logger } from './logger';

/** Only log per-call currency ops when explicitly enabled (avoids console flood in dev). */
const currencyTraceEnabled = () =>
  typeof window !== 'undefined' && localStorage.getItem('VOLUNTAS_DEBUG_CURRENCY') === 'true';

const logCurrencyOperation = (operation: string, details: any) => {
  if (currencyTraceEnabled()) {
    logger.debug(`[Currency] ${operation}:`, details);
  }
};

export const PLAIN_MONEY_DISPLAY_OPTIONS: CurrencyDisplayOptions = {
  compact: false,
  showSymbol: false,
  showCode: false,
  decimals: 0,
};

/**
 * Normalize any amount to USD for comparison
 * 
 * This is the core function for ensuring "apples to apples" comparisons.
 * All Firestore sorting/filtering should use USD-normalized values.
 * 
 * @param amount - Amount in source currency
 * @param currency - Source currency code
 * @param rates - Optional exchange rates (defaults to constants)
 * @returns Amount in USD
 */
export function normalizeToUSD(amount: number, currency: CurrencyCode, rates: ExchangeRates = EXCHANGE_RATES): number {
  if (currency === 'USD') {
    return amount;
  }
  
  const rate = rates[currency];
  const normalized = amount * rate;
  
  logCurrencyOperation('normalizeToUSD', { amount, currency, rate, normalized });
  
  return normalized;
}

/**
 * Convert amount from one currency to another
 * 
 * @param amount - Amount in source currency
 * @param from - Source currency
 * @param to - Target currency
 * @param rates - Optional exchange rates (defaults to constants)
 * @returns Converted amount
 */
export function convertMoney(amount: number, from: CurrencyCode, to: CurrencyCode, rates: ExchangeRates = EXCHANGE_RATES): number {
  if (from === to) {
    return amount;
  }
  
  // Convert to USD first, then to target currency
  const usdAmount = normalizeToUSD(amount, from, rates);
  const targetRate = rates[to];
  const converted = usdAmount / targetRate;
  
  logCurrencyOperation('convertMoney', { amount, from, to, usdAmount, targetRate, converted });
  
  return converted;
}

/**
 * Create Money object from amount and currency
 * 
 * Automatically calculates USD normalization
 * 
 * @param amount - Amount in specified currency
 * @param currency - Currency code
 * @param rates - Optional exchange rates (defaults to constants)
 * @returns Money object
 */
export function createMoney(amount: number, currency: CurrencyCode, rates: ExchangeRates = EXCHANGE_RATES): Money {
  return {
    amount,
    currency,
    amountUSD: normalizeToUSD(amount, currency, rates),
  };
}

/**
 * Detect currency from string input
 * 
 * Checks for currency codes in the string (e.g., "AUD $500K", "$2M USD")
 * Falls back to default currency if none detected
 * 
 * @param input - String that may contain currency code
 * @returns Detected currency code
 */
export function detectCurrency(input: string | null | undefined): CurrencyCode {
  if (!input || typeof input !== 'string') {
    return DEFAULT_CURRENCY;
  }
  
  // Check each currency pattern
  for (const [currency, patterns] of Object.entries(CURRENCY_PATTERNS)) {
    for (const pattern of patterns) {
      if (pattern.test(input)) {
        logCurrencyOperation('detectCurrency', { input, detected: currency });
        return currency as CurrencyCode;
      }
    }
  }
  
  // Default to AUD if no currency detected
  logCurrencyOperation('detectCurrency', { input, detected: 'DEFAULT (AUD)' });
  return DEFAULT_CURRENCY;
}

/**
 * Parse currency string into amount and currency code
 * 
 * Handles formats like:
 * - "AUD $500K"
 * - "$2M USD"
 * - "1500000"
 * 
 * @param input - String to parse
 * @returns ParsedCurrency object
 */
export function parseCurrencyString(input: string | null | undefined): ParsedCurrency {
  if (!input || typeof input !== 'string') {
    return { amount: null, currency: DEFAULT_CURRENCY, original: input || '' };
  }
  
  // Detect currency first
  const currency = detectCurrency(input);
  
  // Remove currency codes and symbols for number extraction
  let cleaned = input
    .replace(/\bAUD\b/gi, '')
    .replace(/\bUSD\b/gi, '')
    .replace(/[£€¥₹$,\s]/g, '')
    .replace(/\([^)]*\)/g, '') // Remove parenthetical content
    .trim();
  
  // Empty after cleaning
  if (!cleaned) {
    return { amount: null, currency, original: input };
  }
  
  // Match number with optional K/M/B suffix
  const match = cleaned.match(/^([0-9.]+)\s*([kmb])?$/i);
  
  if (match) {
    const num = parseFloat(match[1]);
    if (isNaN(num)) {
      return { amount: null, currency, original: input };
    }
    
    const suffix = (match[2] || '').toLowerCase();
    const multipliers: Record<string, number> = {
      'k': 1000,
      'm': 1000000,
      'b': 1000000000,
      '': 1
    };
    
    const amount = num * (multipliers[suffix] || 1);
    
    logCurrencyOperation('parseCurrencyString', { input, amount, currency });
    
    return { amount, currency, original: input };
  }
  
  // Try parsing as plain number
  const plainNum = parseFloat(cleaned);
  if (!isNaN(plainNum)) {
    logCurrencyOperation('parseCurrencyString', { input, amount: plainNum, currency });
    return { amount: plainNum, currency, original: input };
  }
  
  return { amount: null, currency, original: input };
}

/**
 * Format money value for display
 * 
 * Supports:
 * - Currency conversion (source currency → display currency)
 * - Compact notation (K/M/B)
 * - Symbol and code display
 * 
 * @param value - Number or Money object
 * @param sourceCurrency - Currency of the value (if number)
 * @param displayCurrency - Currency to display in
 * @param options - Display options
 * @param rates - Optional exchange rates (defaults to constants)
 * @returns Formatted string
 */
export function formatMoney(
  value: number | Money | null | undefined,
  sourceCurrency: CurrencyCode = DEFAULT_CURRENCY,
  displayCurrency?: CurrencyCode,
  options: CurrencyDisplayOptions = {},
  rates: ExchangeRates = EXCHANGE_RATES
): string {
  // Handle null/undefined
  if (value === null || value === undefined) {
    return 'Not provided';
  }
  
  // Extract amount and currency
  let amount: number;
  let currency: CurrencyCode;
  
  if (typeof value === 'object' && 'amount' in value) {
    amount = value.amount;
    currency = value.currency;
  } else if (typeof value === 'number') {
    amount = value;
    currency = sourceCurrency;
  } else {
    return 'Invalid value';
  }
  
  // Convert to display currency if specified
  const targetCurrency = displayCurrency || currency;
  const displayAmount = currency === targetCurrency 
    ? amount 
    : convertMoney(amount, currency, targetCurrency, rates);
  
  // Apply formatting options
  const {
    showSymbol = true,
    showCode = false,
    compact = true,
    decimals
  } = options;
  
  // Format the number
  let formatted: string;
  
  if (compact && displayAmount >= 1000) {
    // Use compact notation (K/M/B)
    if (displayAmount >= 1000000000) {
      const billions = displayAmount / 1000000000;
      formatted = `${billions.toFixed(decimals ?? 0)}B`;
    } else if (displayAmount >= 1000000) {
      const millions = displayAmount / 1000000;
      formatted = `${millions.toFixed(decimals ?? 0)}M`;
    } else {
      const thousands = displayAmount / 1000;
      formatted = `${thousands.toFixed(decimals ?? 0)}K`;
    }
  } else {
    // Use full number format
    formatted = displayAmount.toLocaleString('en-AU', {
      minimumFractionDigits: decimals ?? 0,
      maximumFractionDigits: decimals ?? 2,
    });
  }
  
  // Add symbol and code
  let result = formatted;
  
  if (showSymbol) {
    result = `${CURRENCY_SYMBOLS[targetCurrency]}${result}`;
  }
  
  if (showCode) {
    result = `${result} ${targetCurrency}`;
  }
  
  logCurrencyOperation('formatMoney', { 
    value, 
    sourceCurrency, 
    displayCurrency: targetCurrency, 
    result 
  });
  
  return result;
}

/**
 * Format money using the Task 4 plain-number standard.
 *
 * - Full number with grouping
 * - No symbol
 * - No compact notation
 * - No currency code
 * - Whole numbers only
 */
export function formatPlainMoney(
  value: number | Money | null | undefined,
  sourceCurrency: CurrencyCode = DEFAULT_CURRENCY,
  displayCurrency?: CurrencyCode,
  rates: ExchangeRates = EXCHANGE_RATES
): string {
  return formatMoney(
    value,
    sourceCurrency,
    displayCurrency,
    PLAIN_MONEY_DISPLAY_OPTIONS,
    rates
  );
}

/**
 * Format money range (e.g., "$2M - $5M")
 * 
 * @param min - Minimum value
 * @param max - Maximum value
 * @param currency - Currency code
 * @param displayCurrency - Display currency
 * @param options - Display options
 * @param rates - Optional exchange rates (defaults to constants)
 * @returns Formatted range string
 */
export function formatMoneyRange(
  min: number | null,
  max: number | null,
  currency: CurrencyCode = DEFAULT_CURRENCY,
  displayCurrency?: CurrencyCode,
  options: CurrencyDisplayOptions = {},
  rates: ExchangeRates = EXCHANGE_RATES
): string {
  if (min === null && max === null) {
    return 'Not specified';
  }
  
  if (min === null) {
    return `Up to ${formatMoney(max!, currency, displayCurrency, options, rates)}`;
  }
  
  if (max === null) {
    return `${formatMoney(min, currency, displayCurrency, options, rates)}+`;
  }
  
  if (min === max) {
    return formatMoney(min, currency, displayCurrency, options, rates);
  }
  
  const formattedMin = formatMoney(min, currency, displayCurrency, { ...options, showCode: false }, rates);
  const formattedMax = formatMoney(max, currency, displayCurrency, options, rates);
  
  return `${formattedMin} - ${formattedMax}`;
}

/**
 * Validate currency code
 * 
 * @param code - Currency code to validate
 * @returns True if valid
 */
export function isValidCurrency(code: string): code is CurrencyCode {
  const validCodes: CurrencyCode[] = ['AUD', 'USD', 'GBP', 'NZD'];
  return validCodes.includes(code as CurrencyCode);
}

/**
 * Get currency symbol
 * 
 * @param currency - Currency code
 * @returns Currency symbol
 */
export function getCurrencySymbol(currency: CurrencyCode): string {
  return CURRENCY_SYMBOLS[currency];
}
